import React from 'react';
import { motion } from 'framer-motion';
import { Check, Users, Heart, BookOpen, Leaf } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';

const About: React.FC = () => {
    const stats = [
        { value: '10+', label: 'Years of Service' },
        { value: '5000+', label: 'Lives Impacted' },
        { value: '200+', label: 'Active Volunteers' },
        { value: '50+', label: 'Communities Served' },
    ];

    const values = [
        {
            icon: <Heart className="w-8 h-8 text-red-500" />,
            title: 'Compassion',
            description: 'We lead with empathy and understanding in all our initiatives.'
        },
        {
            icon: <BookOpen className="w-8 h-8 text-blue-500" />,
            title: 'Education',
            description: 'We believe in the transformative power of knowledge and learning.'
        },
        {
            icon: <Users className="w-8 h-8 text-green-500" />,
            title: 'Community',
            description: 'We build strong, supportive communities that thrive together.'
        },
        {
            icon: <Leaf className="w-8 h-8 text-emerald-500" />,
            title: 'Sustainability',
            description: 'We create solutions that last and respect our environment.'
        },
    ];

    return (
        <div className="min-h-screen flex flex-col bg-gray-50">
            <Header />
            
            {/* Hero Banner */}
            <div className="relative w-full h-[60vh] min-h-[500px] bg-gradient-to-r from-blue-600 to-purple-600 overflow-hidden">
                <div className="absolute inset-0 bg-black/30 z-10" />
                <div className="absolute inset-0 flex items-center justify-center z-20 text-center px-4">
                    <motion.div 
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="max-w-4xl mx-auto"
                    >
                        <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6">
                            Empowering Communities, <span className="text-yellow-300">Transforming Lives</span>
                        </h1>
                        <p className="text-xl text-white/90 max-w-3xl mx-auto mb-8">
                            At Jeevan Jyoti, we're dedicated to creating sustainable change through education, healthcare, and community development initiatives.
                        </p>
                        <button className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-3 rounded-full font-semibold text-lg transition-all duration-300 transform hover:scale-105">
                            Join Our Mission
                        </button>
                    </motion.div>
                </div>
            </div>

            {/* Stats Section */}
            <div className="relative -mt-16 z-30 px-4">
                <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6">
                    {stats.map((stat, index) => (
                        <motion.div 
                            key={index}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.1, duration: 0.5 }}
                            viewport={{ once: true }}
                            className="bg-white p-6 rounded-xl shadow-lg text-center"
                        >
                            <div className="text-3xl font-bold text-blue-600 mb-2">{stat.value}</div>
                            <div className="text-gray-600">{stat.label}</div>
                        </motion.div>
                    ))}
                </div>
            </div>

            {/* Our Story Section */}
            <section className="py-20 px-4">
                <div className="max-w-6xl mx-auto">
                    <motion.div 
                        initial={{ opacity: 0, x: -50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true }}
                        className="mb-16 text-center"
                    >
                        <span className="text-blue-600 font-semibold">Our Journey</span>
                        <h2 className="text-4xl font-bold text-gray-900 mt-2">The Story of Jeevan Jyoti</h2>
                        <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto mt-4 rounded-full" />
                    </motion.div>

                    <div className="grid md:grid-cols-2 gap-12 items-center">
                        <motion.div 
                            initial={{ opacity: 0, x: -50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            viewport={{ once: true }}
                        >
                            <h3 className="text-2xl font-bold text-gray-800 mb-6">From Humble Beginnings to Lasting Impact</h3>
                            <p className="text-gray-600 mb-6">
                                Founded in 2010, Jeevan Jyoti began as a small community initiative with a big dream. What started with just five passionate volunteers has grown into a movement that touches thousands of lives annually across multiple regions.
                            </p>
                            <p className="text-gray-600 mb-8">
                                Our journey has been marked by challenges and triumphs, but through it all, our commitment to creating positive change has remained unwavering. We believe in the power of community and the potential within every individual to make a difference.
                            </p>
                            <button className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-full font-medium hover:opacity-90 transition-opacity">
                                Learn More About Our Work
                            </button>
                        </motion.div>
                        <motion.div 
                            initial={{ opacity: 0, scale: 0.9 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.6, delay: 0.4 }}
                            viewport={{ once: true }}
                            className="relative h-96 rounded-2xl overflow-hidden shadow-xl"
                        >
                            <img 
                                src="/jeevan-jyoti-ngo/images/about-story.jpg" 
                                alt="Our Story"
                                className="w-full h-full object-cover"
                            />
                            <div className="absolute -bottom-6 -right-6 bg-yellow-400 w-48 h-48 rounded-full opacity-20" />
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Vision, Mission & Goals Section */}
            <section className="py-20 bg-gray-50">
                <div className="max-w-6xl mx-auto px-4">
                    <div className="grid md:grid-cols-3 gap-8">
                        {/* Vision */}
                        <motion.div 
                            className="bg-white p-8 rounded-xl shadow-lg"
                            initial={{ opacity: 0, y: 50 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            viewport={{ once: true }}
                        >
                            <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-bold mb-4 text-gray-900">Our Vision</h3>
                            <p className="text-gray-600">To create a society where every individual has access to quality education, healthcare, and opportunities for sustainable development, enabling them to lead a life of dignity and self-reliance.</p>
                        </motion.div>

                        {/* Mission */}
                        <motion.div 
                            className="bg-white p-8 rounded-xl shadow-lg"
                            initial={{ opacity: 0, y: 50 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                            viewport={{ once: true }}
                        >
                            <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mb-6">
                                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-bold mb-4 text-gray-900">Our Mission</h3>
                            <p className="text-gray-600">To empower underprivileged communities through education, healthcare, and skill development programs, fostering sustainable change and creating pathways out of poverty.</p>
                        </motion.div>

                        {/* Goals */}
                        <motion.div 
                            className="bg-white p-8 rounded-xl shadow-lg"
                            initial={{ opacity: 0, y: 50 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.4 }}
                            viewport={{ once: true }}
                        >
                            <div className="w-14 h-14 bg-purple-100 rounded-full flex items-center justify-center mb-6">
                                <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-bold mb-4 text-gray-900">Our Goals</h3>
                            <ul className="space-y-3 text-gray-600">
                                <li className="flex items-start">
                                    <Check className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                                    <span>Provide quality education to 10,000+ children annually</span>
                                </li>
                                <li className="flex items-start">
                                    <Check className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                                    <span>Establish 50+ skill development centers by 2025</span>
                                </li>
                                <li className="flex items-start">
                                    <Check className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                                    <span>Reduce school dropout rates by 50% in our operational areas</span>
                                </li>
                            </ul>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Our Values */}
            <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
                <div className="max-w-6xl mx-auto px-4">
                    <motion.div 
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <span className="text-blue-600 font-semibold">What We Stand For</span>
                        <h2 className="text-4xl font-bold text-gray-900 mt-2">Our Core Values</h2>
                        <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto mt-4 rounded-full" />
                    </motion.div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {values.map((value, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1, duration: 0.5 }}
                                viewport={{ once: true }}
                                className="bg-white p-8 rounded-xl shadow-md hover:shadow-xl transition-shadow duration-300"
                            >
                                <div className="w-16 h-16 bg-blue-50 rounded-xl flex items-center justify-center mb-6">
                                    {value.icon}
                                </div>
                                <h3 className="text-xl font-bold text-gray-800 mb-3">{value.title}</h3>
                                <p className="text-gray-600">{value.description}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 px-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
                <div className="max-w-4xl mx-auto text-center">
                    <motion.h2 
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true }}
                        className="text-3xl md:text-4xl font-bold mb-6"
                    >
                        Ready to Make a Difference?
                    </motion.h2>
                    <motion.p 
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        viewport={{ once: true }}
                        className="text-xl mb-8 text-blue-100 max-w-2xl mx-auto"
                    >
                        Join our community of changemakers and help us create a brighter future for all.
                    </motion.p>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.4 }}
                        viewport={{ once: true }}
                        className="flex flex-col sm:flex-row gap-4 justify-center"
                    >
                        <button className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-3 rounded-full font-semibold transition-colors">
                            Donate Now
                        </button>
                        <button className="bg-transparent border-2 border-white hover:bg-white/10 px-8 py-3 rounded-full font-semibold transition-colors">
                            Become a Volunteer
                        </button>
                    </motion.div>
                </div>
            </section>
            
            <Footer />
        </div>
    );
};

export default About;